import { downloadOfflineAssets, type OfflineAsset } from '~/utils/offlineAssets'

type NetworkInformation = EventTarget & {
  saveData?: boolean
  effectiveType?: string
}

export default defineNuxtPlugin((nuxtApp) => {
  if (!('caches' in window) || !('serviceWorker' in navigator)) return

  const connection = (
    navigator as Navigator & { connection?: NetworkInformation }
  ).connection
  let timer: ReturnType<typeof setTimeout> | undefined
  let active: AbortController | undefined
  let navigating = false
  let complete = false

  const canDownload = () =>
    navigator.onLine &&
    !document.hidden &&
    !navigating &&
    !connection?.saveData &&
    !['slow-2g', '2g'].includes(connection?.effectiveType || '')

  function pause() {
    clearTimeout(timer)
    active?.abort()
  }

  function schedule() {
    pause()
    if (complete || !canDownload()) return
    // Give initial rendering, splash, and route requests priority over offline preparation.
    timer = setTimeout(() => void run(), 10_000)
  }

  async function run() {
    if (!canDownload()) return
    const controller = new AbortController()
    active = controller
    const { signal } = controller
    try {
      // Wait for the small app-shell installation before filling its media cache.
      await navigator.serviceWorker.ready
      if (signal.aborted) return
      const fetchAsset = async (url: string) => {
        const request = new AbortController()
        const abort = () => request.abort()
        signal.addEventListener('abort', abort, { once: true })
        if (signal.aborted) abort()
        const timeout = setTimeout(abort, 30_000)
        try {
          const response = await fetch(url, {
            credentials: 'omit',
            cache: 'no-cache',
            // Bypass the worker's CacheFirst route when refreshing a changed file.
            headers: { 'x-wlearn-offline-download': '1' },
            signal: request.signal,
          })
          // Include the body in the timeout, rather than just waiting for headers.
          await response.clone().arrayBuffer()
          return response
        } finally {
          clearTimeout(timeout)
          signal.removeEventListener('abort', abort)
        }
      }
      const response = await fetchAsset('/offline-assets.json')
      if (!response.ok) return
      const assets: OfflineAsset[] = await response.json()
      const cache = await caches.open('learning-assets-v1')
      const revisions = await caches.open('learning-asset-revisions-v1')
      const yieldToPage = () =>
        new Promise<void>((resolve) => {
          // One file at a time, with breathing room and a browser-idle opportunity.
          let idle: number | undefined
          const done = () => {
            clearTimeout(delay)
            if (idle !== undefined) window.cancelIdleCallback(idle)
            signal.removeEventListener('abort', done)
            resolve()
          }
          const delay = setTimeout(() => {
            if ('requestIdleCallback' in window)
              idle = window.requestIdleCallback(done, { timeout: 2000 })
            else done()
          }, 1000)
          signal.addEventListener('abort', done, { once: true })
          if (signal.aborted) done()
        })
      const downloaded = await downloadOfflineAssets(
        assets,
        cache,
        revisions,
        fetchAsset,
        yieldToPage,
        signal,
      )
      if (!signal.aborted) complete = downloaded
    } catch {
      // Offline, quota, and network failures must never interrupt the learner.
      // Successful files stay cached; a later online/visibility/navigation event resumes.
    } finally {
      if (active === controller) {
        active = undefined
        if (!complete && !signal.aborted && canDownload())
          timer = setTimeout(() => void run(), 60_000)
      }
    }
  }

  nuxtApp.hook('page:start', () => {
    navigating = true
    pause()
  })
  nuxtApp.hook('page:finish', () => {
    navigating = false
    schedule()
  })
  nuxtApp.hook('app:mounted', () => {
    if (document.readyState === 'complete') schedule()
    else window.addEventListener('load', schedule, { once: true })
  })
  window.addEventListener('online', schedule)
  window.addEventListener('offline', pause)
  document.addEventListener('visibilitychange', schedule)
  connection?.addEventListener('change', schedule)
})
